import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../primitives/Icon';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/cn';
import './chat.css';

export const SuggestionChip = ({ label, onClick }) => (
  <Button variant="outline" size="sm" onClick={onClick}>
    {label}
  </Button>
);

export const AttachedChip = ({ icon, label, onRemove }) => (
  <Badge variant="secondary">
    <Icon name={icon} size={14} />
    <span>{label}</span>
    {onRemove && (
      <button
        type="button"
        className="q-chip-x"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
      >
        <Icon name="x" data-icon="inline-start" />
      </button>
    )}
  </Badge>
);

// File-type tint for the attachment tile, like an app logo in a connect card.
const FILE_TINT = {
  xlsx: '#107C41', xls: '#107C41', xlsm: '#107C41', csv: '#15B042', tsv: '#15B042',
  json: '#D97A1F', parquet: '#5B5BD6',
};

const fileExt = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
};

// Header of the attachment tray: an icon tile and the file name, with a quiet
// remove button — the "Connect gmail" bar sitting above the composer.
const AttachedFile = ({ item, onRemove }) => {
  const ext = fileExt(item.name);
  const icon = item.kind === 'dataset'
    ? (ext === 'json' ? 'file-json' : 'file-spreadsheet')
    : item.kind === 'model' ? 'brain' : 'line-chart';
  return (
    <div className="q-tray__file">
      <span className="q-tray__tile" style={{ color: FILE_TINT[ext] || 'var(--foreground)' }} aria-hidden>
        <Icon name={icon} size={18} strokeWidth={1.75} />
      </span>
      <span className="q-tray__name" title={item.name}>{item.name}</span>
      {onRemove && (
        <button
          type="button"
          className="q-tray__remove"
          onClick={onRemove}
          aria-label={`Remove ${item.name}`}
          title="Remove"
        >
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
  );
};

const AddMenu = ({ open, onClose, onAttachClick, onConnectSourceClick, placement }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.parentElement.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const items = [
    onAttachClick && {
      key: 'upload', icon: 'paperclip', label: 'Upload files',
      hint: 'CSV, Excel or JSON from your computer', onClick: onAttachClick,
    },
    onConnectSourceClick && {
      key: 'connect', icon: 'database', label: 'Data connectors',
      hint: 'Pull tables from your data warehouse', onClick: onConnectSourceClick,
    },
  ].filter(Boolean);

  return (
    <div ref={ref} className={cn('q-addmenu', `q-addmenu--${placement}`)} role="menu">
      {items.map(it => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          className="q-addmenu__item"
          onClick={() => { onClose(); it.onClick(); }}
        >
          <Icon name={it.icon} size={18} />
          <span className="q-addmenu__label">{it.label}</span>
          <span className="q-addmenu__hint">{it.hint}</span>
        </button>
      ))}
    </div>
  );
};

const Composer = ({
  value, onChange, onSend,
  attached, onRemoveAttached, onAttachClick, onConnectSourceClick,
  suggestions, onSuggestionClick,
  showSuggestions, busy,
  attachPlaceholder,
  variant = 'dock',
}) => {
  const taRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const adjust = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(220, ta.scrollHeight) + 'px';
  };

  const canSend = ((value || '').trim() || (attached && attached.length > 0)) && !busy;
  const hasAdd = !!(onAttachClick || onConnectSourceClick);
  const hasAttached = !!(attached && attached.length > 0);

  return (
    <div className={cn('q-composer-wrap', variant === 'center' && 'q-composer-wrap--center')}>
      {showSuggestions && suggestions && suggestions.length > 0 && (
        <div className="q-fade-in q-suggest-row">
          {suggestions.map((s, i) => (
            <SuggestionChip key={i} label={s.label} onClick={() => onSuggestionClick(s)} />
          ))}
        </div>
      )}

      <div className={cn('q-composer-anchor', hasAttached && 'q-tray')}>
        {hasAttached && (
          <div className="q-tray__head">
            {attached.map(a => (
              <AttachedFile
                key={a.id}
                item={a}
                onRemove={onRemoveAttached ? () => onRemoveAttached(a.id) : undefined}
              />
            ))}
          </div>
        )}
        <div
          className="ui-composer"
          onClick={(e) => { if (e.target === e.currentTarget) taRef.current?.focus(); }}
        >

          <div className="ui-composer__row">
            {hasAdd && (
              <button
                type="button"
                className={cn('ui-composer__icon', menuOpen && 'ui-composer__icon--on')}
                onClick={() => setMenuOpen(o => !o)}
                title="Add data"
                aria-label="Add data"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <Icon name="plus" size={20} strokeWidth={1.75} />
              </button>
            )}

            <textarea
              ref={taRef}
              className="ui-composer__input"
              value={value}
              onChange={(e) => { onChange(e.target.value); adjust(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (canSend) onSend(); }
              }}
              placeholder={attachPlaceholder || 'Ask anything about your data'}
              rows={1}
            />

            <button
              type="button"
              className="ui-composer__send"
              onClick={canSend ? onSend : undefined}
              disabled={!canSend}
              title={busy ? 'Working' : 'Send'}
              aria-label={busy ? 'Working' : 'Send'}
            >
              {busy ? (
                <Icon name="loader-2" size={18} style={{ animation: 'qSpin 800ms linear infinite' }} />
              ) : (
                <Icon name="arrow-up" size={18} strokeWidth={2.25} />
              )}
            </button>
          </div>
        </div>

        <AddMenu
          open={menuOpen}
          onClose={closeMenu}
          onAttachClick={onAttachClick}
          onConnectSourceClick={onConnectSourceClick}
          placement={variant === 'center' ? 'below' : 'above'}
        />
      </div>

      {variant !== 'center' && (
        <div className="ui-composer__note">
          QAgent is an AI system operated by Qombra B.V. Estimates only. Verify before acting on results.
        </div>
      )}
    </div>
  );
};

export default Composer;
